import {userInfo, getApi, resetApi, logout, clearBackendSession, type UserInfo, getImpersonator, isPasswordless} from "../auth";
import $ from "jquery";
import {DOMLoaded} from "../events";
import {navigateTo, isNullish, returnUrl} from "../utils";
import UI from "../ui";
import {trace} from "../trace";
import LoggedUserBox from "../components/LoggedUserBox";
import SignInModal from "../components/modals/SignInModal";
import PrivacyModal from "../components/modals/PrivacyModal";
import {consensiUtente} from "../privacy";
import "../globals";
import Newsletter from "../components/newsletter";
import { t } from "~/hooks/useI18n";

interface Spinner {
    spin: () => Promise<void>;
    stop: () => void;
}

const fixUserInfo = async (user: UserInfo, serverUser: string) : Promise<UserInfo> => {
    let fix = false;
    if (user.isAuthenticated && !serverUser) {
        // è scaduto l'access token oppure è stato chiamato logout sul be.
        //     => tento il refresh. Se fallisce, proseguo con user anonimo.
        console.warn("Sessione backend scaduta. Rinfresco...");
        fix = true;
    }
    if (!user.isAuthenticated && serverUser) {
        console.warn("Sessione backend attiva ma utente non autenticato lato client. Rinfresco...");
        fix = true;
    }
    
    if (fix) {
        try {
            user = await userInfo(true);
            if (!user.isAuthenticated) {
                await clearBackendSession();
            }
        } catch (error) {
            console.log(error);
            user = {isAuthenticated: false};
        }
    }
    return user;
}


abstract class Page {
    private name: string;
    private title: string;
    private _loggedUserBox: LoggedUserBox
    private _signInModal: SignInModal | undefined;
    private _privacyModal: PrivacyModal;
    private _newsletter: Newsletter | undefined;
    protected signInModalEnabled: boolean = true;
    protected newsletterEnabled: boolean| undefined;
    protected checkUserOnInit: boolean = true;
    protected checkPrivacy: boolean = true;

    public get loggedUserBox(): LoggedUserBox {
        return this.loggedUserBox!;
    }

    public get spinner() : Spinner {
        return {
            spin: async () => {
                await UI.showWait();
            },
            stop: () => {
                UI.hideWait();
            }
        };
    }
    public get signInModal(): SignInModal | undefined {
        return this._signInModal;
    }

    public get privacyModal(): PrivacyModal {
        return this._privacyModal;
    }

    public get newsletter(): Newsletter | undefined {
        return this._newsletter;
    }

    protected constructor(name: string, title: string) {
        this.name = name;
        this.title = title;

        // Imposta il default solo se non è già stato impostato dalla classe derivata
        if (this.newsletterEnabled === undefined) {
            this.newsletterEnabled = true;
        }

        this._loggedUserBox = new LoggedUserBox();
        if (this.signInModalEnabled)
            this._signInModal = new SignInModal();
        this._privacyModal = new PrivacyModal();
        if (this.newsletterEnabled)
            this._newsletter = new Newsletter();
    }


    private static _current: Page;

    public static get current(): Page {
        return Page._current;
    }

    private static set current(value: Page) {
        Page._current = value;
    }

    public get backendSessionUser() : string {
        return $(document.body).data('user');
    } 

    // inizializzazioni di pagina eseguite appena possibile (prima del caricamento del DOM)
    protected pageInit(): void {
        return
    }

    // inizializzazioni di pagina eseguite dopo il caricamento del DOM
    abstract pageStart(): Promise<void>; 

    public async init(): Promise<void> {
        // logica di inizializzazione comune a tutte le pagine
        //console.log(`Page ${this.name}  initialized...`);
        Page.current = this;
        window.ThisPage = this; // per compatibilità con le vecchie pagine
        
        // Verifica autenticazione e caricamento del menu utente
        let user = await this.initUser();
        
        // inizializzazioni di pagina pre-DOM
        this.pageInit();

        await DOMLoaded();
        UI.bindWaiting();

        this.initTooltips();
        
        if (this.checkPrivacy) {
            await this.initPrivacy(user);
        }
        await this.initNewsletter();

        if (this.signInModalEnabled)
            await this.initLogin();

        //TODO: gestione del urlRefer ovvero il redirect dopo il login in caso di atterraggio anonimo in una pagina protetta

        // proseguo con l'inizializzazione specifica della pagina
        try {
            await this.pageStart();
        } catch (error) {
            console.error("Error during page start: ", error);
            // gestire l'errore
        }
        window.addEventListener('pageshow', (event) => {
            //sugli eventi di history back/forward, se la pagina è stata caricata dalla cache del browser
            //alert("pageshow event, persisted: " + event.persisted);
            UI.hideWait();
            if (event.persisted) {
                resetApi();
                this.loadUserMenu();
            }
        });
        
    }
    private initTooltips(): void {
        console.log( "Initializing tooltips...");
        $(document).ready(() => {
            console.log($('[data-bs-toggle="tooltip"]'));
            $('[data-bs-toggle="tooltip"]').tooltip();
        });
    }
    
    protected async checkPasswordScaduta(): Promise<void> {
        let user = await userInfo();
        const impersonator = await getImpersonator();
        const skipPass = await isPasswordless();
        if (user.isPasswordScaduta && impersonator == null  && !skipPass) {
            await navigateTo(window.BaseUrlWeb + "cambio-password");
        }
    }
    
    private async initUser(): Promise<UserInfo> {
        let user: UserInfo = { isAuthenticated: false };
        
        if (!this.checkUserOnInit) { // se la pagina non vuole che venga verificata l'autenticazione all'inizio (es login), 
                                    // restituisco un utente anonimo senza fare ulteriori controlli
           return user;
        }
        
        let serverUser = this.backendSessionUser;
        
        try {
            user = await fixUserInfo(await userInfo(), serverUser);
                       
            if (user.isAuthenticated) {
                await this.checkPasswordScaduta();
                // Se l'utente è autenticato, mostrare il profilo
                //console.log("User is authenticated");
                //console.log(userInfo);
                //let _ = this.uiSetTokenFields(user.anagraficaId, user.accessToken); // non c'è bisogno di aspettare
                await this.loadUserMenu();
                let _ = trace();
            } else {
                if ($("body").data("user")) {
                    // la sessione sul server è attiva ma non lo è sul client. Probabilmente è scaduta
                    await clearBackendSession();
                    this.showLoginButton();
                }
            }
        } catch (error) {
            // errore sul codice di autenticazione, cancello le credenziali
            console.error("===> checkAuth error: ", error);
            // Non eseguire logout se errore di rete (server non raggiungibile in locale)
            const isNetworkError = error instanceof TypeError && (error as TypeError).message === "Failed to fetch";
            if (!isNetworkError) {
                await logout(); // se logout fa redirect possibile refresh loop
            }
        } finally {
            this._loggedUserBox?.show();
        }   
        return user;
    }
    
    
    private async initPrivacy(userInfo: UserInfo): Promise<void> {
        const impersonator = await getImpersonator();
        await this._privacyModal.activate();

        if (userInfo.isAuthenticated && userInfo.consensiIncompleti
            && impersonator == null) {
            await this.acquisisciConsensi();
        }

    }

    private async initNewsletter(): Promise<void> {
        if (!this.newsletterEnabled || !this._newsletter) {
            return;
        }

        try {
            await this._newsletter.activate();
            console.log("Newsletter component initialized");
        } catch (error) {
            console.error("Error initializing newsletter component:", error);
        }
    }

    public async acquisisciConsensi(): Promise<void> {
        this.spinner.stop();
        this._privacyModal.show();
        let esito = await this._privacyModal.acquisisciConsensi();
        this._privacyModal.destroy();
        UI.showWait();
        if (esito === "cancelled") {
            await logout(); //slogga e torna alla home 
        } else if (esito === "saved") {
            await navigateTo(window.BaseUrlWeb + "dashboard");
        }
    }

    private async initLogin(): Promise<void> {
        await this._signInModal!.activate();

        this._signInModal!.onLogin(async (user: UserInfo) => {
            getApi().clearCached("userMenu");
            this._signInModal!.hide();
            const impersonator = await getImpersonator();
            
            if (!user.isEmailConfirmed) {
                await getApi().fetchWithAuth("POST /resendConfirmationEmail", {
                    body: JSON.stringify({
                        email: user.email,
                        clientId: window.WsAuthClientCode,
                        callbackUrl: window.BaseUrlWeb + "Home/ConfirmEmail"
                    })
                });
                //TODO: I18n
                let _ = UI.confirmOneButton("", "Il tuo indirizzo E-mail non è ancora stato verificato.<br/>" +
                    "Per accedere, clicca sul link nell’E-mail di conferma che ti è stata inviata.");

            } else if (user.isPasswordScaduta && impersonator == null) {
                await navigateTo(window.BaseUrlWeb + "cambio-password");
            } else if (user.consensiIncompleti) {
                await this.acquisisciConsensi()
            } else {
                await this.loadUserMenu(returnUrl() || window.BaseUrlWeb + "dashboard");
            }

        });
    }
    
    private showLoginButton(): void {
        if (this.signInModalEnabled) {
            $("#btnSignIn").siblings().remove();
            $("#btnSignIn").removeClass("d-none").show();
            $("#loggedUserBox").remove();
            this._signInModal?.hide();
        }
    }
    
    public async loadUserMenu(redirectUrl?: string, refresh: boolean = false): Promise<string> {
        const auth = getApi();
        let menuHtml : string = "";
        let user : UserInfo | null = null;
        console.log("LoadUserMenu...");
        if (refresh) {
            auth.clearCached("userInfo");
            auth.clearCached("userMenu");
            user = await userInfo(true);
        } else {
            menuHtml = auth.getCached("userMenu");
            user = auth.getCached("userInfo");
        }
        

        const _displayMenuAndResolve = async (data: any) => {
            returnUrl(null); // resetto la landing url
            if (redirectUrl) {
                
                await navigateTo(redirectUrl);
            }
            await this._loggedUserBox
                ?.html(data).activate();

        }

        if (menuHtml && this.backendSessionUser === user?.email) {
            await _displayMenuAndResolve(menuHtml);
            return menuHtml;
        }

        // Se il menu non è presente, oppure la sessione server non è sincronizzata, lo carico dal server
        this._loggedUserBox?.hide();
        let userMenuUrl = window.BaseUrlWeb + "Home/UserMenu" + (refresh ? "?refresh=true" : "");// + (user.maskUserId ? "?IdAspNetMask=" + (user.maskUserId) : "");

        try {
            menuHtml = await auth.fetchWithAuth("GET " + userMenuUrl);
            auth.setCached("userMenu", menuHtml, {bindAuth: true}); 

            await _displayMenuAndResolve(menuHtml);
            
            return menuHtml;
        } catch(error) {
            console.error("Error loading user menu: ", error);
            this.showLoginButton();
            return "";
        } finally {
            this._loggedUserBox?.show();
        }
    }


    private async uiSetTokenFields(anagId: number | undefined, token: string | undefined): Promise<void> {
        if (!anagId || !token)
            return Promise.resolve();


        await DOMLoaded();

        $(
            "input[name='UserId']," +
            "input[name='userid']," +
            "input[name='userAnagraficaId']"
        ).val(anagId);

        $("input[name='token']").val(token); //TODO: valutare se serve. L'autorizzazione verso il backend dovrà essere gestita tramite cookie

    }
}

export default Page;

export {type Page};
